import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { Controller, useFormContext } from "react-hook-form";
import slugify from "slugify";

import { Card } from "src/base-components/Card";
import { ErrorHint } from "src/base-components/ErrorHint";
import { Input } from "src/base-components/Input";
import { Label } from "src/base-components/Label";
import { MAX_DESCRIPTION_LENGTH } from "src/cases/caseView/constants";
import { CaseTemplateFormValues } from "src/cases/templates/caseTemplateFormUtils";
import { TemplateDescriptionEditor } from "src/cases/templates/common/TemplateDescriptionEditor";
import { useIsTemplateVersionPublished } from "src/cases/templates/utils";

type ItemType = "action" | "task";

type ValidatorFunction =
  | ((value: string) => true | string)
  | ((value: string, formValues: CaseTemplateFormValues) => true | string);

type TemplateItemInfoProps = {
  itemIndex: number;
  itemType: ItemType;
  nameMaxLength: number;
  keyMaxLength: number;
  nameValidators: Record<string, ValidatorFunction>;
  keyValidators: Record<string, ValidatorFunction>;
  descriptionPlaceholder?: string;
  nameDisabled?: boolean;
  keyDisabled?: boolean;
  descriptionDisabled?: boolean;
};

export const TemplateItemInfo: React.FC<TemplateItemInfoProps> = ({
  itemIndex,
  itemType,
  nameMaxLength,
  keyMaxLength,
  nameValidators,
  keyValidators,
  descriptionPlaceholder = "Write a description for the reviewer",
  nameDisabled = false,
  keyDisabled = false,
  descriptionDisabled = false,
}) => {
  const form = useFormContext<CaseTemplateFormValues>();
  const { errors } = form.formState;
  const isPublished = useIsTemplateVersionPublished();

  const fieldPath = itemType === "action" ? "case_actions" : "case_tasks";
  const itemLabel = itemType === "action" ? "Action" : "Task";

  const itemErrors =
    itemType === "action"
      ? errors.attributes?.case_actions?.[itemIndex]
      : errors.attributes?.case_tasks?.[itemIndex];

  return (
    <Card className="my-2 w-full">
      <div className="flex flex-col gap-y-3">
        <div className="flex flex-1 flex-col">
          <Label required>Name</Label>
          <Input
            data-loc={`${itemType}-name-input`}
            disabled={nameDisabled}
            errored={!!itemErrors?.display_name}
            placeholder="Name"
            fullWidth
            {...form.register(
              `attributes.${fieldPath}.${itemIndex}.display_name`,
              {
                required: `${itemLabel} name is required`,
                validate: nameValidators,
                maxLength: {
                  value: nameMaxLength,
                  message: `${itemLabel} name must not be more than ${nameMaxLength} characters`,
                },
                onChange: (event) => {
                  const { isDirty } = form.getFieldState(
                    `attributes.${fieldPath}.${itemIndex}.key`,
                  );

                  if (!isDirty && !isPublished) {
                    form.setValue(
                      `attributes.${fieldPath}.${itemIndex}.key`,
                      slugify(event.target.value, {
                        lower: true,
                        strict: true,
                        replacement: "_",
                      }),
                      { shouldValidate: true },
                    );
                  }
                },
                deps: `attributes.${fieldPath}.${itemIndex}.key`,
              },
            )}
          />
          {itemErrors?.display_name && (
            <ErrorHint>{itemErrors.display_name.message}</ErrorHint>
          )}
        </div>

        <div className="flex flex-1 flex-col">
          <Label required>Key</Label>
          <Input
            data-loc={`${itemType}-key-input`}
            disabled={keyDisabled}
            errored={!!itemErrors?.key}
            placeholder="Key"
            fullWidth
            monospaced
            {...form.register(`attributes.${fieldPath}.${itemIndex}.key`, {
              required: `${itemLabel} key is required`,
              validate: keyValidators,
              maxLength: {
                value: keyMaxLength,
                message: `${itemLabel} key must not be more than ${keyMaxLength} characters`,
              },
              deps: `attributes.${fieldPath}.${itemIndex}.display_name`,
            })}
            suffixIcon={{
              icon: faInfoCircle,
              helpText:
                "A unique identifier for this field, to be referenced in the Decision Flow.",
            }}
          />
          {itemErrors?.key && <ErrorHint>{itemErrors.key.message}</ErrorHint>}
        </div>

        <div className="flex flex-1 flex-col">
          <Label>Describe the {itemType}</Label>
          <Controller
            control={form.control}
            name={`attributes.${fieldPath}.${itemIndex}.description`}
            render={({ field, fieldState }) => (
              <>
                <TemplateDescriptionEditor
                  data-loc={`${itemType}-description-editor`}
                  disabled={descriptionDisabled}
                  placeholder={descriptionPlaceholder}
                  value={field.value ?? ""}
                  onChange={(value) => {
                    field.onChange(value);
                    form.trigger(
                      `attributes.${fieldPath}.${itemIndex}.description`,
                    );
                  }}
                />
                {fieldState.error && (
                  <ErrorHint>{fieldState.error.message}</ErrorHint>
                )}
              </>
            )}
            rules={{
              maxLength: {
                value: MAX_DESCRIPTION_LENGTH,
                message: `${itemLabel} description must not be more than ${MAX_DESCRIPTION_LENGTH.toLocaleString()} characters`,
              },
            }}
          />
        </div>
      </div>
    </Card>
  );
};
